/**
 * Container for the "Discard extraction" confirmation dialog.
 *
 * Wires the Discard button to the `deleteContractExtraction` mutation.
 * The mutation's required `confirmation` argument is hardcoded to
 * `"confirm"` because the UI no longer exposes a type-to-confirm input
 * (per the Story 2.2 two-button AlertDialog AC).
 * @module features/contracts/components/DiscardExtractionDialog/DiscardExtractionDialogContainer
 */

import { AlertTriangle } from "lucide-react-native";
import React, { memo, useCallback } from "react";

import ConfirmDialog from "@/components/molecules/ConfirmDialog";

import { useDeleteContractExtraction } from "@/features/contracts/hooks/useDeleteContractExtraction";
import { useIsContractOwner } from "@/features/contracts/hooks/useIsContractOwner";

/**
 * Confirmation string required by the backend `deleteContractExtraction`
 * mutation. The server validates this value case-insensitively against
 * the literal "confirm".
 * @see backend-v2/src/contracts/contracts.service.ts
 */
const DELETE_CONFIRMATION_TOKEN = "confirm";

/** Props for {@link DiscardExtractionDialogContainer}. */
interface Props {
  /** Whether the dialog is currently visible. */
  readonly isOpen: boolean;
  /** Invoked when the dialog should close. */
  readonly onClose: () => void;
  /** UUID of the player whose extraction data will be discarded. */
  readonly playerId: string;
  /** Document type whose extraction data will be discarded. */
  readonly documentType: string;
  /** Invoked after the extraction data is successfully discarded. */
  readonly onDeleted: () => void;
}

/**
 * Container component for the discard-extraction confirmation dialog.
 * @param props - Component props
 * @param props.isOpen - Whether the dialog is visible
 * @param props.onClose - Callback invoked when the dialog is dismissed
 * @param props.playerId - Player UUID whose extraction data will be discarded
 * @param props.documentType - Document type whose extraction data will be discarded
 * @param props.onDeleted - Callback invoked after successful discard
 */
function DiscardExtractionDialogContainer({
  isOpen,
  onClose,
  playerId,
  documentType,
  onDeleted,
}: Props) {
  const isOwner = useIsContractOwner();
  const { deleteExtraction, isDeleting } = useDeleteContractExtraction();

  const handleClose = useCallback(() => {
    if (!isDeleting) {
      onClose();
    }
  }, [isDeleting, onClose]);

  const handleConfirm = useCallback(async () => {
    if (isDeleting) {
      return;
    }
    const ok = await deleteExtraction(
      playerId,
      documentType,
      DELETE_CONFIRMATION_TOKEN
    );
    if (ok) {
      onDeleted();
      onClose();
    }
  }, [
    isDeleting,
    deleteExtraction,
    playerId,
    documentType,
    onDeleted,
    onClose,
  ]);

  // SE-4350 defense-in-depth: refuse to render the dialog for
  // non-OWNER callers. The drawer already hides the Discard
  // button that opens this dialog, but a direct embedder could
  // still set `isOpen` — the null short-circuit guarantees the
  // mutation path stays unreachable for non-OWNERs.
  if (!isOwner) return null;

  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title="Discard extraction?"
      body="Discarding will delete this extraction and all its data. This cannot be undone."
      icon={AlertTriangle}
      tone="danger"
      cancelLabel="Continue Editing"
      confirmLabel={isDeleting ? "Discarding..." : "Discard"}
      isConfirming={isDeleting}
      cancelTestID="continue-editing-btn"
      confirmTestID="confirm-discard-btn"
    />
  );
}

DiscardExtractionDialogContainer.displayName =
  "DiscardExtractionDialogContainer";

export default memo(DiscardExtractionDialogContainer);
